/**
 * Live cursor presence for the inspiration board.
 *
 * Each active user writes a single doc at
 * `portalProjectBoards/{dealId}/presence/{uid}` with their cursor position in
 * flow (canvas) coordinates, throttled by the caller. Peers subscribe to the
 * subcollection and render cursors; docs older than {@link BOARD_PRESENCE_STALE_MS}
 * are treated as gone (covers closed tabs where the delete never ran).
 */
import { db } from './firebase';
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';

export type BoardCursorSurface = 'board' | 'floorplan';

export interface BoardCursorPresence {
  uid: string;
  name: string;
  color: string;
  /** Flow coordinates on the board, or plan inches inside a floor plan. */
  x: number;
  y: number;
  surface: BoardCursorSurface;
  /** Floor-plan node id when `surface` is `floorplan`. */
  nodeId?: string;
  updatedAt: Date | null;
}

export const BOARD_PRESENCE_STALE_MS = 25_000;

const CURSOR_COLORS = [
  '#6366f1', // indigo
  '#0ea5e9', // sky
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // red
  '#d946ef', // fuchsia
  '#14b8a6', // teal
  '#f97316', // orange
];

/** Stable per-user cursor color derived from the uid. */
export function cursorColorForUid(uid: string): string {
  let hash = 0;
  for (let i = 0; i < uid.length; i++) {
    hash = (hash * 31 + uid.charCodeAt(i)) | 0;
  }
  return CURSOR_COLORS[Math.abs(hash) % CURSOR_COLORS.length];
}

function presenceDoc(dealId: string, uid: string) {
  return doc(db, 'portalProjectBoards', dealId, 'presence', uid);
}

export function isFreshBoardCursor(c: BoardCursorPresence, now = Date.now()): boolean {
  return !!c.updatedAt && now - c.updatedAt.getTime() < BOARD_PRESENCE_STALE_MS;
}

/** All peer cursors (self excluded). Staleness is re-checked by the caller. */
export function subscribeBoardPresence(
  dealId: string,
  selfUid: string,
  callback: (cursors: BoardCursorPresence[]) => void,
): () => void {
  return onSnapshot(
    collection(db, 'portalProjectBoards', dealId, 'presence'),
    (snap) => {
      const cursors = snap.docs
        .map((d) => {
          const v = d.data();
          const updatedAtRaw = v.updatedAt as { toDate?: () => Date } | undefined;
          const surface: BoardCursorSurface = v.surface === 'floorplan' ? 'floorplan' : 'board';
          const cursor: BoardCursorPresence = {
            uid: d.id,
            name: String(v.name || 'Someone'),
            color: String(v.color || CURSOR_COLORS[0]),
            x: Number(v.x) || 0,
            y: Number(v.y) || 0,
            surface,
            nodeId: v.nodeId ? String(v.nodeId) : undefined,
            updatedAt: updatedAtRaw?.toDate?.() ?? null,
          };
          return cursor;
        })
        .filter((c) => c.uid !== selfUid);
      callback(cursors);
    },
    (err) => {
      console.warn('[boardPresenceService] subscribe failed', err);
      callback([]);
    },
  );
}

export async function publishBoardCursor(
  dealId: string,
  cursor: {
    uid: string;
    name: string;
    color: string;
    x: number;
    y: number;
    surface?: BoardCursorSurface;
    nodeId?: string;
  },
): Promise<void> {
  try {
    const surface = cursor.surface || 'board';
    await setDoc(
      presenceDoc(dealId, cursor.uid),
      {
        name: cursor.name,
        color: cursor.color,
        x: surface === 'floorplan' ? Math.round(cursor.x * 10) / 10 : Math.round(cursor.x),
        y: surface === 'floorplan' ? Math.round(cursor.y * 10) / 10 : Math.round(cursor.y),
        surface,
        nodeId: surface === 'floorplan' && cursor.nodeId ? cursor.nodeId : null,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  } catch (err) {
    console.warn('[boardPresenceService] publish failed', err);
  }
}

export async function clearBoardCursor(dealId: string, uid: string): Promise<void> {
  try {
    await deleteDoc(presenceDoc(dealId, uid));
  } catch {
    /* stale-filtering handles missed deletes */
  }
}
